import { useLanguage } from '../contexts/LanguageContext';
import AssuredWorkloadIcon from '@mui/icons-material/AssuredWorkload';
import DocumentScannerOutlinedIcon from '@mui/icons-material/DocumentScannerOutlined';
import InsightsOutlinedIcon from '@mui/icons-material/InsightsOutlined';
import { useEffect, useRef } from 'react';

export default function AccountingService() {
    const { t } = useLanguage();
    const sectionRef = useRef<HTMLElement>(null);
    const logoRef = useRef<HTMLDivElement>(null);
    const titleRef = useRef<HTMLParagraphElement>(null);
    const descriptionRef = useRef<HTMLParagraphElement>(null);
    const cardsRef = useRef<HTMLDivElement>(null);
    const cardRefs = useRef<(HTMLDivElement | null)[]>([]);

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        // Animate logo
                        if (entry.target === logoRef.current) {
                            entry.target.classList.add('animate-fade-in-up');
                        }
                        // Animate title
                        if (entry.target === titleRef.current) {
                            setTimeout(() => {
                                entry.target.classList.add('animate-fade-in-up');
                            }, 200);
                        }
                        // Animate description
                        if (entry.target === descriptionRef.current) {
                            setTimeout(() => {
                                entry.target.classList.add('animate-fade-in-up');
                            }, 400);
                        }
                        // Animate cards container
                        if (entry.target === cardsRef.current) {
                            setTimeout(() => {
                                entry.target.classList.add('animate-fade-in-up');
                            }, 600);
                        }
                        // Animate individual cards with staggered delay
                        if (cardRefs.current.includes(entry.target as HTMLDivElement)) {
                            const index = cardRefs.current.indexOf(entry.target as HTMLDivElement);
                            setTimeout(() => {
                                // For staggered cards, we only animate opacity, not translate-y
                                entry.target.classList.add('opacity-100');
                            }, 800 + (index * 200)); // 800ms base delay + 200ms per card
                        }
                    }
                });
            },
            { threshold: 0.1 }
        );

        // Observe all elements
        if (logoRef.current) observer.observe(logoRef.current);
        if (titleRef.current) observer.observe(titleRef.current);
        if (descriptionRef.current) observer.observe(descriptionRef.current);
        if (cardsRef.current) observer.observe(cardsRef.current);
        cardRefs.current.forEach((card) => {
            if (card) observer.observe(card);
        });

        return () => observer.disconnect();
    }, []);

    return (
        <section ref={sectionRef} id="accountingService" className="h-[160vh] md:h-[100vh] relative mt-5 bg-transparent py-8 md:py-0">
            
            <div className='justify-center items-center text-[#244473] mx-10 md:ltr:ps-10 pt-[5rem] ' >
                <p 
                    ref={titleRef}
                    className='text-4xl font-bold mb-5 opacity-0 translate-y-8 transition-all duration-700 ease-out hover:scale-105 hover:text-blue-600 cursor-pointer'
                >
                    {t("services.accounting.title")}
                </p>
                <p 
                    ref={descriptionRef}
                    className='text-sm md:text-lg mb-5 opacity-0 translate-y-8 transition-all duration-700 ease-out hover:text-blue-600 cursor-pointer'
                >
                    {t("services.accounting.description")}
                </p>
                <div 
                    ref={cardsRef}
                    className="grid md:grid-cols-3 grid-cols-1 gap-5 w-full mt-10 opacity-0 translate-y-8 transition-all duration-700 ease-out hover:scale-105 group"
                >
                    <div 
                        ref={(el) => { cardRefs.current[0] = el; }}
                        className="bg-[#d9d9d9] items-center justify-center self-center text-center rounded-lg py-5 px-2 opacity-0 transition-all duration-700 ease-out hover:scale-105 hover:bg-blue-100 group cursor-pointer"
                    >
                        <AssuredWorkloadIcon 
                            sx={{ fontSize: { xs: 30, md: 70 } }} 
                            className='mb-5 text-center group-hover:text-blue-600 group-hover:rotate-12 transition-all duration-300' 
                        />
                        <p className='text-xl font-semibold mb-5 group-hover:text-blue-600 transition-colors duration-300'>
                            {t("services.accounting.cards.card1.title")}
                        </p>
                        <p className='text-sm md:text-base group-hover:text-blue-700 transition-colors duration-300'>
                            {t("services.accounting.cards.card1.description")}
                        </p>
                    </div>
                    <div 
                        ref={(el) => { cardRefs.current[1] = el; }}
                        className="bg-[#d9d9d9] items-center justify-center self-center text-center rounded-lg py-5 -translate-y-[-20%] px-2 opacity-0 transition-all duration-700 ease-out hover:scale-105 hover:bg-blue-100 group cursor-pointer"
                    >
                        <DocumentScannerOutlinedIcon 
                            sx={{ fontSize: { xs: 30, md: 70 } }} 
                            className='mb-5 group-hover:text-blue-600 group-hover:rotate-12 transition-all duration-300' 
                        />
                        <p className='text-xl font-semibold mb-5 group-hover:text-blue-600 transition-colors duration-300'>
                            {t("services.accounting.cards.card2.title")}
                        </p>
                        <p className='text-sm md:text-base group-hover:text-blue-700 transition-colors duration-300'>
                            {t("services.accounting.cards.card2.description")}
                        </p>
                    </div>
                    <div 
                        ref={(el) => { cardRefs.current[2] = el; }}
                        className="bg-[#d9d9d9] items-center justify-center self-center text-center rounded-lg py-5 -translate-y-[-40%] px-2 opacity-0 transition-all duration-700 ease-out hover:scale-105 hover:bg-blue-100 group cursor-pointer"
                    >
                        <InsightsOutlinedIcon 
                            sx={{ fontSize: { xs: 30, md: 70 } }} 
                            className='mb-5 group-hover:text-blue-600 group-hover:rotate-12 transition-all duration-300' 
                        />
                        <p className='text-xl font-semibold mb-5 group-hover:text-blue-600 transition-colors duration-300'>
                            {t("services.accounting.cards.card3.title")}
                        </p>
                        <p className='text-sm md:text-base group-hover:text-blue-700 transition-colors duration-300'>
                            {t("services.accounting.cards.card3.description")}
                        </p>
                    </div>
                </div>

            </div>
        </section>
    );
}